import React from "react";
import ReactDOM from "react-dom/client";
import "./main.css";

const API_URL = "https://eiko-bau-mobileapp.de/api.php";
const LS_KEY = "auth_user_v1";

/* =========================
   App Shell
========================= */
function App() {
  const [user, setUser] = React.useState(null);
  const [showProfile, setShowProfile] = React.useState(false);

  React.useEffect(() => {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return;
    try {
      setUser(JSON.parse(raw));
    } catch {
      localStorage.removeItem(LS_KEY);
    }
  }, []);

  function handleLoggedIn(u) {
    setUser(u);
    localStorage.setItem(LS_KEY, JSON.stringify(u));
  }

  function logout() {
    setUser(null);
    setShowProfile(false);
    localStorage.removeItem(LS_KEY);
  }

  return (
    <div id="app" className="app">
      <header id="appHeader" className="app__header">
        <div className="brand">
          <img
            src="https://eiko-bau.de/mobile_logo.png"
            alt="Eiko Bau Logo"
            className="brand__logo"
          />
        </div>

        <div className="header__right">
          {user ? (
            <div className="profilemenu">
              <button
                id="btnProfile"
                className="userpill"
                type="button"
                onClick={() => setShowProfile((p) => !p)}
                aria-haspopup="menu"
                aria-expanded={showProfile ? "true" : "false"}
              >
                <div className="userpill__stack">
                  <span className="userpill__name">
                    {user.name} {user.nachname}
                  </span>
                  <div className="userpill__role">
                    {String(user.rolle || "").toLowerCase() === "admin"
                      ? "Chef"
                      : String(user.rolle || "").charAt(0).toUpperCase() + String(user.rolle || "").slice(1)}
                  </div>
                </div>
              </button>

              <div className={`menu ${showProfile ? "is-open" : ""}`} role="menu">
                <button
                  id="btnLogout"
                  className="btn btn--primary menu__item"
                  type="button"
                  onClick={logout}
                >
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <span className="muted">Nicht eingeloggt</span>
          )}
        </div>
      </header>

      <main id="appMain" className="app__main">
        {!user ? (
          <div className="container container--narrow">
            <section className="card card--glass" id="loginCard">
              <div className="card__head">
                <h1 className="h1">Login</h1>
                <p className="muted">
                  Melde dich mit deinem Loginname und Passwort an.
                </p>
              </div>
              <div className="card__body">
                <Login onLoggedIn={handleLoggedIn} />
              </div>
            </section>
          </div>
        ) : user.rolle === "admin" ? (
          <Admin user={user} />
        ) : (
          <EmployeeHome user={user} />
        )}
      </main>
    </div>
  );
}

/* =========================
   Login
========================= */
function Login({ onLoggedIn }) {
  const [form, setForm] = React.useState({ loginname: "", passwort: "" });
  const [msg, setMsg] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  function onChange(e) {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setMsg("");
    setBusy(true);

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "login", ...form }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Unbekannter Fehler");
      if (!data?.user) throw new Error("Login-Antwort ist ungültig (user fehlt).");

      onLoggedIn(data.user);
    } catch (err) {
      setMsg("❌ " + (err?.message || "Fehler"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form id="loginForm" className="form" onSubmit={onSubmit}>
      <div className="field">
        <label className="label" htmlFor="loginname">
          Loginname
        </label>
        <input
          id="loginname"
          className="input"
          name="loginname"
          value={form.loginname}
          onChange={onChange}
          autoComplete="username"
          required
        />
      </div>

      <div className="field">
        <label className="label" htmlFor="passwort">
          Passwort
        </label>
        <input
          id="passwort"
          className="input"
          type="password"
          name="passwort"
          value={form.passwort}
          onChange={onChange}
          autoComplete="current-password"
          required
        />
      </div>

      <div className="form__actions">
        <button id="btnLogin" className="btn btn--primary" type="submit" disabled={busy}>
          {busy ? "Einloggen…" : "Einloggen"}
        </button>
      </div>

      {msg ? (
        <div id="loginMsg" className="notice notice--error">
          {msg}
        </div>
      ) : null}
    </form>
  );
}

/* =========================
   Employee Home
========================= */
function EmployeeHome({ user }) {
  const [page, setPage] = React.useState("menu");
  const [provision, setProvision] = React.useState(Number(user?.provisionmonat || 0));
  const [gehalt, setGehalt] = React.useState(Number(user?.gehalt || 0));
  const [summe, setSumme] = React.useState(Number(user?.gehalt || 0) + Number(user?.provisionmonat || 0));
  const [projects, setProjects] = React.useState([]);
  const [selectedProject, setSelectedProject] = React.useState(null);
  const [options, setOptions] = React.useState([]);
  const [selectedOptionIds, setSelectedOptionIds] = React.useState(() => new Set());
  const [msg, setMsg] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [myRequests, setMyRequests] = React.useState([]);

  const selectedOptions = options.filter((o) => selectedOptionIds.has(String(o.id)));
  const previewEur = (percent) => {
    const p = Number(percent || 0);
    const g = Number(gehalt || 0);
    return (g * (p / 100));
  };
  const selectedSumEur = selectedOptions.reduce((s, o) => s + previewEur(o.percent), 0);

  async function loadProjects() {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "list_projects" }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || "Fehler beim Laden der Projekte");
    setProjects(Array.isArray(data.projects) ? data.projects : []);
  }

  async function loadOptionsForProject(projectId) {
    if (!projectId) return;
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ 
        action: "list_options_for_project",
        project_id: projectId 
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || "Fehler beim Laden der Optionen");
    setOptions(Array.isArray(data.options) ? data.options : []);
  }

  async function loadPaycard() {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "get_paycard", mitarbeiter_id: user.id }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || "Fehler beim Laden der Paycard");
    setProvision(Number(data?.provision || 0));
    setGehalt(Number(data?.gehalt || 0));
    setSumme(Number(data?.summe || 0));
  }

  async function loadMyRequests() {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "list_requests_employee", mitarbeiter_id: user.id }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || "Fehler beim Laden deiner Anfragen");
    setMyRequests(Array.isArray(data.requests) ? data.requests : []);
  }

  React.useEffect(() => {
    (async () => {
      setMsg("");
      try {
        await Promise.all([loadProjects(), loadPaycard(), loadMyRequests()]);
      } catch (e) {
        setMsg("❌ " + (e?.message || "Fehler"));
      }
    })();
  }, []);

  React.useEffect(() => {
    if (selectedProject) {
      loadOptionsForProject(selectedProject.id).catch(e => {
        setMsg("❌ " + (e?.message || "Fehler beim Laden der Optionen"));
      });
    }
  }, [selectedProject]);

  function toggleOption(id) {
    setSelectedOptionIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleProjectSelect(project) {
    setSelectedProject(project);
    setSelectedOptionIds(new Set());
  }

  async function submitRequest() {
    setMsg("");

    if (!selectedProject) {
      setMsg("❌ Bitte zuerst ein Projekt auswählen.");
      return;
    }
    if (selectedOptionIds.size <= 0) {
      setMsg("❌ Bitte mindestens eine Option auswählen.");
      return;
    }

    setBusy(true);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "create_request",
          mitarbeiter_id: user.id,
          project_id: selectedProject.id,
          option_ids: Array.from(selectedOptionIds),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Fehler beim Absenden");

      setMsg(`✅ ${data.ids?.length || 0} Anfragen gesendet`);
      setSelectedOptionIds(new Set());
      setSelectedProject(null);

      await Promise.all([loadPaycard(), loadMyRequests()]);
      setPage("menu");
    } catch (e) {
      setMsg("❌ " + (e?.message || "Fehler"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container">
      <section className="card card--glass">
        <div className="card__body">
          <div className="paycardGrid">
            <div className="paycell">
              <div className="paylabel">Gehalt</div>
              <div className="payvalue">{gehalt.toFixed(2).replace(".", ",")} €</div>
            </div>
            <div className="paycell paycell--right">
              <div className="paylabel">
                Provision {new Date().toLocaleDateString("de-DE", { month: "long" })}
              </div>
              <div className="payvalue payvalue--green">+{provision.toFixed(2).replace(".", ",")} €</div>
            </div>
            <div className="paycell paycell--sum">
              <div className="paylabel">Finales Gehalt</div>
              <div className="payvalue payvalue--sum">{summe.toFixed(2).replace(".", ",")} €</div>
            </div>
          </div>
        </div>
      </section>

      <div className="spacer" />

      <section id="employeeMenu">
        <button className="tile" type="button" onClick={() => setPage("create")}>
          <div className="tile__title">Neue Anfrage</div>
          <div className="tile__desc">Option(en) auswählen</div>
        </button>
        <button className="tile" type="button" onClick={() => setPage("my_requests")}>
          <div className="tile__title">Meine Anfragen</div>
          <div className="tile__desc">Status ansehen</div>
        </button>
      </section>

      <div className="spacer" />

      {page === "my_requests" ? (
        <section className="card">
          <div className="card__head">
            <h2 className="h2">Meine Anfragen</h2>
            <p className="muted">Status deiner gemeldeten Optionen.</p>
          </div>
          <div className="card__body">
            <button className="btn btn--ghost" onClick={loadMyRequests} disabled={busy} style={{ marginBottom: 12 }}>
              {busy ? "Lade…" : "Neu laden"}
            </button>
            {myRequests.length === 0 ? (
              <div className="notice notice--info">Du hast diesen Monat noch keine Anfragen.</div>
            ) : (
              <div className="cards" id="myRequestsGrid">
                {myRequests.map((r) => {
                  const status = String(r.status || "offen").toLowerCase();
                  const percent = Number(r.provision_percent ?? r.provision ?? 0);
                  const eurFixed = r.provision_eur != null ? Number(r.provision_eur) : null;
                  const eurPreview = previewEur(percent);
                  return (
                    <div className="card card--mini" key={r.id}>
                      <div className="card__miniTop">
                        <div className="card__miniTitle">
                          <b>{r.name || "Anfrage"}</b>
                        </div>
                        <span className={`badge badge--status badge--status-${status}`}>{r.status || "offen"}</span>
                      </div>
                      <div className="card__miniBody">
                        <div className="muted">
                          {r.erstellt_am
                            ? new Date(r.erstellt_am).toLocaleDateString("de-DE", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : "-"}
                        </div>
                        <div className="kv" style={{ marginTop: 8 }}>
                          <div className="kv__k">Prozent</div>
                          <div className="kv__v">{percent.toFixed(1).replace(".", ",")} %</div>
                        </div>
                        <div className="kv">
                          <div className="kv__k">Provision (€)</div>
                          <div className="kv__v">
                            {eurFixed != null
                              ? `${eurFixed.toFixed(2).replace(".", ",")} €`
                              : `≈ ${eurPreview.toFixed(2).replace(".", ",")} €`}
                          </div>
                        </div>
                      </div>
                      <div className="card__miniFoot muted">
                        {eurFixed != null ? "Fixiert nach Bestätigung" : "Vorschau anhand deines Gehalts"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      ) : null}

      {page === "create" ? (
        <>
          <section className="card">
            <div className="card__head">
              <h2 className="h2">Projekt auswählen</h2>
              <p className="muted">Danach Option(en) auswählen und absenden.</p>
            </div>
            <div className="card__body">
              {projects.length === 0 ? (
                <div className="notice notice--info">Keine Projekte gefunden.</div>
              ) : (
                <div className="cards cards--projects">
                  {projects.map((p) => (
                    <button key={p.id} type="button" className="projectcard" onClick={() => handleProjectSelect(p)}>
                      <div className="projectcard__top">
                        <div className="projectcard__title">{p.name}</div>
                      </div>
                      {p.adresse ? <div className="projectcard__address">{p.adresse}</div> : null}
                      {p.beschreibung ? <div className="projectcard__desc">{p.beschreibung}</div> : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {selectedProject ? (
            <div className="modal-overlay" onClick={() => setSelectedProject(null)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <section className="card" style={{ position: "relative" }}>
                  <button
                    className="btn btn--ghost"
                    type="button"
                    onClick={() => setSelectedProject(null)}
                    style={{ position: "absolute", top: "12px", right: "12px", fontSize: "24px", lineHeight: "1" }}
                  >
                    ✕
                  </button>
                  <div className="card__head">
                    <div className="h2extraproj">{selectedProject.name}</div>
                    <div className="muted">Optionen sind % (Provision = % vom Gehalt)</div>
                  </div>
                  <div className="card__body">
                    {options.length === 0 ? (
                      <div className="notice notice--info">
                        Diesem Projekt sind noch keine Optionen zugewiesen. Bitte Admin informieren.
                      </div>
                    ) : (
                      <div className="extras__grid">
                        {options.map((x) => {
                          const pct = Number(x.percent || 0);
                          const eur = previewEur(pct);
                          return (
                            <label
                              key={x.id}
                              className={`extraitem ${selectedOptionIds.has(String(x.id)) ? "is-on" : ""}`}
                            >
                              <input
                                className="extraitem__check"
                                type="checkbox"
                                checked={selectedOptionIds.has(String(x.id))}
                                onChange={() => toggleOption(x.id)}
                              />
                              <span className="extraitem__label">{x.label}</span>
                              <span className="extraitem__amount">
                                {pct.toFixed(1).replace(".", ",")} %
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <div className="extras__footer">
                      <div className="extras__sum">
                        In EUR: <b>{selectedSumEur.toFixed(2).replace(".", ",")} €</b>
                      </div>
                      <button
                        className="btn btn--primary"
                        type="button"
                        onClick={submitRequest}
                        disabled={busy || options.length === 0}
                      >
                        {busy ? "Sende…" : "Absenden"}
                      </button>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {msg ? (
        <div className={`notice ${msg.startsWith("✅") ? "notice--success" : "notice--error"}`} style={{ marginTop: 12 }}>
          {msg}
        </div>
      ) : null}
    </div>
  );
}

/* =========================
   Admin
========================= */
function Admin({ user }) {
  const [page, setPage] = React.useState("chefmenu");

  const cards = [
    { id: "show_requests", title: "Offene Provisionsanfragen", desc: "Anfragen ansehen & bearbeiten", icon: "📋" },
    { id: "show_projects", title: "Projekte anzeigen", desc: "Alle Projekte als Karten", icon: "🏗️" },
    { id: "create_project", title: "Projekt anlegen", desc: "Neues Projekt erstellen", icon: "➕" },
    { id: "create_user", title: "Mitarbeiter anlegen", desc: "Neuen Mitarbeiter/Admin erstellen", icon: "👤" },
    { id: "manage_employees", title: "Mitarbeiter verwalten", desc: "Mitarbeiter bearbeiten und löschen", icon: "👥" },
    { id: "show_employees", title: "Mitarbeiterübersicht", desc: "Tabelle: Gehalt + Provision + Gehalt", icon: "📊" },
    { id: "manage_options", title: "Optionen verwalten", desc: "Antwortoptionen (in %) pflegen", icon: "⚙️" },
  ];

  function PageShell({ title, children, noCard = false }) {
    return (
      <div className="container">
        <div className="toolbar" id="adminToolbar">
          <div className="toolbar__left">
            <button className="btn btn--ghost" id="btnBack" onClick={() => setPage("chefmenu")}>
              Zurück
            </button>
            <h1 className="h1 h1--tight">{title}</h1>
          </div>
          <div className="toolbar__right" />
        </div>
        {noCard ? children : <section className="card">{children}</section>}
      </div>
    );
  }

  if (page === "chefmenu") {
    return (
      <div className="container">
        <section className="hero" id="adminHero">
          <div className="hero__left">
            <h1 className="h1">Admin Dashboard</h1>
          </div>
        </section>
        <section className="grid" id="adminGrid">
          {cards.map((c) => (
            <button key={c.id} id={`nav_${c.id}`} className="tile" onClick={() => setPage(c.id)} type="button">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className="tile__icon">{c.icon}</div>
                <div className="tile__title">{c.title}</div>
              </div>
            </button>
          ))}
        </section>
      </div>
    );
  }

  if (page === "create_user") {
    return (
      <PageShell title="Mitarbeiter anlegen">
        <div className="card__body">
          <CreateUser adminUser={user} />
        </div>
      </PageShell>
    );
  }

  if (page === "create_project") {
    return (
      <PageShell title="Projekt anlegen">
        <div className="card__body">
          <CreateProject adminUser={user} />
        </div>
      </PageShell>
    );
  }

  if (page === "show_projects") {
    return (
      <PageShell title="Projekte" noCard>
        <ShowProjects adminUser={user} />
      </PageShell>
    );
  }

  if (page === "show_requests") {
    return (
      <PageShell title="Provisionsanfragen" noCard>
        <ShowRequests adminUser={user} />
      </PageShell>
    );
  }

  if (page === "manage_employees") {
    return (
      <PageShell title="Mitarbeiter verwalten" noCard>
        <ManageEmployees adminUser={user} />
      </PageShell>
    );
  }

  if (page === "show_employees") {
    return (
      <PageShell title="Mitarbeiterübersicht" noCard>
        <ShowEmployees adminUser={user} />
      </PageShell>
    );
  }

  if (page === "manage_options") {
    return (
      <PageShell title="Optionen verwalten">
        <div className="card__body">
          <ManageOptions adminUser={user} />
        </div>
      </PageShell>
    );
  }

  return (
    <div className="container">
      <div className="notice notice--error">Unbekannte Seite.</div>
      <button className="btn btn--ghost" onClick={() => setPage("chefmenu")}>
        Zurück zum Menü
      </button>
    </div>
  );
}

/* =========================
   Admin: Create User
========================= */
function CreateUser({ adminUser }) {
  const [form, setForm] = React.useState({
    name: "",
    nachname: "",
    loginname: "",
    passwort: "",
    rolle: "mitarbeiter",
    gehalt: "",
  });
  const [msg, setMsg] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  function onChange(e) {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setMsg("");
    setBusy(true);

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "create_user", admin_id: adminUser.id, ...form }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Unbekannter Fehler");

      setMsg(`✅ User angelegt`);
      setForm({ name: "", nachname: "", loginname: "", passwort: "", rolle: "mitarbeiter", gehalt: "" });
    } catch (err) {
      setMsg("❌ " + (err?.message || "Fehler"));
    } finally {
      setBusy(false);
    }
  }

  const isOk = msg.startsWith("✅");

  return (
    <div id="createUser" className="panel">
      <form id="createUserForm" className="form" onSubmit={onSubmit}>
        <div className="grid2">
          <div className="field">
            <label className="label" htmlFor="cu_name">Vorname</label>
            <input id="cu_name" className="input" name="name" value={form.name} onChange={onChange} required />
          </div>
          <div className="field">
            <label className="label" htmlFor="cu_nachname">Nachname</label>
            <input id="cu_nachname" className="input" name="nachname" value={form.nachname} onChange={onChange} required />
          </div>
        </div>
        <div className="grid2">
          <div className="field">
            <label className="label" htmlFor="cu_loginname">Loginname</label>
            <input id="cu_loginname" className="input" name="loginname" value={form.loginname} onChange={onChange} required />
          </div>
          <div className="field">
            <label className="label" htmlFor="cu_passwort">Passwort</label>
            <input
              id="cu_passwort"
              className="input"
              type="password"
              name="passwort"
              value={form.passwort}
              onChange={onChange}
              autoComplete="new-password"
              required
            />
          </div>
        </div>
        <div className="grid2">
          <div className="field">
            <label className="label" htmlFor="cu_rolle">Rolle</label>
            <select id="cu_rolle" className="select" name="rolle" value={form.rolle} onChange={onChange}>
              <option value="mitarbeiter">Mitarbeiter</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="cu_gehalt">Gehalt (€)</label>
            <input
              id="cu_gehalt"
              className="input"
              name="gehalt"
              inputMode="decimal"
              placeholder=""
              value={form.gehalt}
              onChange={onChange}
            />
          </div>
        </div>
        <div className="form__actions">
          <button id="btnCreateUser" className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? "Speichere…" : "Anlegen"}
          </button>
        </div>
      </form>
      {msg ? <div className={`notice ${isOk ? "notice--success" : "notice--error"}`}>{msg}</div> : null}
    </div>
  );
}

/* =========================
   Admin: Create Project
========================= */
function CreateProject({ adminUser }) {
  const [form, setForm] = React.useState({ name: "", adresse: "", beschreibung: "" });
  const [msg, setMsg] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  function onChange(e) {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setMsg("");
    setBusy(true);

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "create_project",
          admin_id: adminUser.id,
          name: form.name,
          adresse: form.adresse,
          beschreibung: form.beschreibung,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Unbekannter Fehler");

      setMsg(`✅ Projekt angelegt`);
      setForm({ name: "", adresse: "", beschreibung: "" });
    } catch (err) {
      setMsg("❌ " + (err?.message || "Fehler"));
    } finally {
      setBusy(false);
    }
  }

  const isOk = msg.startsWith("✅");

  return (
    <div id="createProject" className="panel">
      <form id="createProjectForm" className="form" onSubmit={onSubmit}>
        <div className="field">
          <label className="label" htmlFor="cp_name">Projektname</label>
          <input id="cp_name" className="input" name="name" value={form.name} onChange={onChange} required />
        </div>
        <div className="field">
          <label className="label" htmlFor="cp_adresse">Adresse</label>
          <input
            id="cp_adresse"
            className="input"
            name="adresse"
            value={form.adresse}
            onChange={onChange}
            placeholder=""
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="cp_beschreibung">Beschreibung</label>
          <textarea
            id="cp_beschreibung"
            className="textarea"
            name="beschreibung"
            value={form.beschreibung}
            onChange={onChange}
            rows={5}
          />
        </div>
        <div className="form__actions">
          <button id="btnCreateProject" className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? "Speichere…" : "Anlegen"}
          </button>
        </div>
      </form>
      {msg ? <div className={`notice ${isOk ? "notice--success" : "notice--error"}`}>{msg}</div> : null}
    </div>
  );
}

/* =========================
   Admin: Show Projects
========================= */
function ShowProjects({ adminUser }) {
  const [projects, setProjects] = React.useState([]);
  const [msg, setMsg] = React.useState("Lade Projekte...");
  const [busy, setBusy] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(null);

  async function load() {
    setBusy(true);
    setMsg("Lade Projekte...");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "list_projects" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Unbekannter Fehler");
      setProjects(Array.isArray(data.projects) ? data.projects : []);
      setMsg("");
    } catch (err) {
      setMsg("❌ " + (err?.message || "Fehler"));
      setProjects([]);
    } finally {
      setBusy(false);
    }
  }

  async function deleteProject(projectId) {
    setBusy(true);
    setMsg("");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete_project", admin_id: adminUser.id, project_id: projectId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Löschen fehlgeschlagen");
      await load();
    } catch (e) {
      setMsg("❌ " + (e?.message || "Fehler"));
    } finally {
      setBusy(false);
    }
  }

  React.useEffect(() => { load(); }, []);

  return (
    <div id="showProjects" className="panel">
      {msg ? <div className="notice notice--info">{msg}</div> : null}
      {!msg && projects.length === 0 ? <div className="notice notice--info">Keine Projekte gefunden.</div> : null}
      <div className="cards" id="projectsGrid">
        {projects.map((p) => (
          <div className="card card--mini" id={`project_${p.id ?? "x"}`} key={p.id ?? `${p.name}-${p.erstellt_am}`}>
            <div className="card__miniTop">
              <div className="card__miniTitle">{p.name}</div>
              <button className="trash" type="button" onClick={() => setConfirmDelete(p.id)} title="Projekt löschen" disabled={busy}>
                🗑️
              </button>
            </div>
            <div className="card__miniBody">
              {p.beschreibung ? <div className="card__miniDesc">{p.beschreibung}</div> : null}
              {p.adresse ? <div className="card__miniAddress">{p.adresse}</div> : null}
            </div>
            <div className="card__miniFoot muted">
              Erstellt am:{" "}
              {p.erstellt_am
                ? new Date(p.erstellt_am).toLocaleDateString("de-DE", { year: "numeric", month: "long", day: "numeric" })
                : "-"}
            </div>
          </div>
        ))}
      </div>
      {confirmDelete ? (
        <ConfirmModal
          title="Projekt löschen"
          message="Dieses Projekt wirklich löschen? (Anfragen zu diesem Projekt werden auch gelöscht)"
          onConfirm={() => {
            deleteProject(confirmDelete);
            setConfirmDelete(null);
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      ) : null}
    </div>
  );
}

/* =========================
   Admin: Show Requests
========================= */
function ShowRequests({ adminUser }) {
  const [requests, setRequests] = React.useState([]);
  const [msg, setMsg] = React.useState("Lade Anfragen...");
  const [busy, setBusy] = React.useState(false);

  async function load() {
    setBusy(true);
    setMsg("Lade Anfragen...");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "list_requests_admin" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Unbekannter Fehler");
      setRequests(Array.isArray(data.requests) ? data.requests : []);
      setMsg("");
    } catch (err) {
      setMsg("❌ " + (err?.message || "Fehler"));
      setRequests([]);
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(requestId, status) {
    setMsg("");
    setBusy(true);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "set_request_status", admin_id: adminUser.id, request_id: requestId, status }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Status konnte nicht geändert werden");
      await load();
    } catch (e) {
      setMsg("❌ " + (e?.message || "Fehler"));
    } finally {
      setBusy(false);
    }
  }

  React.useEffect(() => { load(); }, []);

  return (
    <div id="showRequests" className="panel">
      {msg ? <div className="notice notice--info">{msg}</div> : null}
      {!msg && requests.length === 0 ? (
        <div className="notice notice--info">Zurzeit liegen keine offenen Anfragen vor.</div>
      ) : null}
      <div className="cards" id="requestsGrid">
        {requests.map((r, idx) => {
          const key = r.id ?? `${r.name}-${r.erstellt_am}-${idx}`;
          const status = String(r.status || "offen").toLowerCase();
          const percent = Number(r.provision_percent ?? r.provision ?? 0);
          const eur = Number(r.provision_eur_view ?? 0);
          return (
            <div className="card card--mini" id={`request_${r.id ?? idx}`} key={key}>
              <div className="card__miniTop">
                <div className="card__miniTitle" style={{ fontWeight: "bold", fontSize: "inherit" }}>
                  {r.mitarbeiter_vorname || r.mitarbeiter_nachname
                    ? `${r.mitarbeiter_vorname || ""} ${r.mitarbeiter_nachname || ""}`.trim()
                    : `#${r.mitarbeiter_id ?? "-"}`}
                </div>
                <span className={`badge badge--status badge--status-${status}`}>{r.status || "offen"}</span>
              </div>
              <div className="card__miniBody">
                <div style={{ marginBottom: 6 }}>
                  <strong>{r.name || "Anfrage"}</strong>
                </div>
                <div className="muted" style={{ marginBottom: 10 }}>
                  Datum:{" "}
                  {r.erstellt_am
                    ? new Date(r.erstellt_am).toLocaleDateString("de-DE", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "-"}
                </div>
                <div className="kv">
                  <div className="kv__k">Prozent</div>
                  <div className="kv__v">{percent.toFixed(1).replace(".", ",")} %</div>
                </div>
                <div className="kv" style={{ marginBottom: 10 }}>
                  <div className="kv__k">In EUR</div>
                  <div className="kv__v">{eur.toFixed(2).replace(".", ",")} €</div>
                </div>
                <div className="adminActions">
                  <button
                    className="actionbtn actionbtn--ok"
                    type="button"
                    disabled={busy}
                    onClick={() => setStatus(r.id, "bestaetigt")}
                    title="Bestätigen"
                  >
                    ✓ Bestätigen
                  </button>
                  <button
                    className="actionbtn actionbtn--no"
                    type="button"
                    disabled={busy}
                    onClick={() => setStatus(r.id, "abgelehnt")}
                    title="Ablehnen"
                  >
                    ✕ Ablehnen
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =========================
   Admin: Manage Options (mit Projektzuweisung)
========================= */
function ManageOptions({ adminUser }) {
  const [rows, setRows] = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState("Lade Optionen...");
  const [editingOption, setEditingOption] = React.useState(null);
  const [confirmDelete, setConfirmDelete] = React.useState(null);
  const [create, setCreate] = React.useState({ label: "", percent: "" });

  async function loadProjects() {
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "list_projects" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Fehler beim Laden der Projekte");
      setProjects(Array.isArray(data.projects) ? data.projects : []);
    } catch (e) {
      console.error(e);
    }
  }

  async function load() {
    setBusy(true);
    setMsg("Lade Optionen...");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "list_options" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Fehler");
      setRows(Array.isArray(data.options) ? data.options : []);
      setMsg("");
    } catch (e) {
      setRows([]);
      setMsg("❌ " + (e?.message || "Fehler"));
    } finally {
      setBusy(false);
    }
  }

  React.useEffect(() => { 
    load(); 
    loadProjects();
  }, []);

  async function createOption(e) {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "create_option",
          admin_id: adminUser.id,
          label: create.label,
          percent: create.percent,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Fehler");
      setCreate({ label: "", percent: "" });
      await load();
      setMsg("✅ Option angelegt");
    } catch (e2) {
      setMsg("❌ " + (e2?.message || "Fehler"));
    } finally {
      setBusy(false);
    }
  }

  async function updateOption(id, patch) {
    setBusy(true);
    setMsg("");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "update_option",
          admin_id: adminUser.id,
          id,
          ...patch,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Fehler");
      await load();
      setMsg("✅ Option aktualisiert");
    } catch (e) {
      setMsg("❌ " + (e?.message || "Fehler"));
    } finally {
      setBusy(false);
    }
  }

  async function deleteOption(id) {
    setBusy(true);
    setMsg("");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete_option", admin_id: adminUser.id, id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Fehler");
      await load();
      setMsg("✅ Option gelöscht");
    } catch (e) {
      setMsg("❌ " + (e?.message || "Fehler"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel" id="manageOptions">
      {msg ? <div className="notice notice--info">{msg}</div> : null}
      <form className="form" onSubmit={createOption} style={{ marginTop: 10 }}>
        <div className="grid2">
          <div className="field">
            <label className="label">Name</label>
            <input
              className="input"
              value={create.label}
              onChange={(e) => setCreate((p) => ({ ...p, label: e.target.value }))}
              required
            />
          </div>
          <div className="field">
            <label className="label">Provision</label>
            <input
              className="input"
              inputMode="decimal"
              placeholder="%"
              step="0.1"
              value={create.percent}
              onChange={(e) => setCreate((p) => ({ ...p, percent: e.target.value }))}
              required
            />
          </div>
        </div>
        <div className="form__actions">
          <button className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? "Speichere…" : "Option anlegen"}
          </button>
        </div>
      </form>
      <div className="divider" />
      {rows.length === 0 ? (
        <div className="notice notice--info">Noch keine Optionen vorhanden.</div>
      ) : (
        <div className="tablewrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 70 }}>ID</th>
                <th>Name</th>
                <th style={{ width: 300 }}>Prozent</th>
                <th style={{ width: 100 }}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <OptionRow
                  key={r.id}
                  row={r}
                  projects={projects}
                  adminUser={adminUser}
                  busy={busy}
                  onSave={(patch) => updateOption(r.id, patch)}
                  onDelete={() => setConfirmDelete(r.id)}
                  onEdit={() => setEditingOption(r)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editingOption ? (
        <EditOptionModal
          option={editingOption}
          projects={projects}
          adminUser={adminUser}
          onClose={() => setEditingOption(null)}
          onUpdate={() => {
            load();
            setEditingOption(null);
          }}
        />
      ) : null}
      {confirmDelete ? (
        <ConfirmModal
          title="Option löschen"
          message="Diese Option wirklich löschen?"
          onConfirm={() => {
            deleteOption(confirmDelete);
            setConfirmDelete(null);
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      ) : null}
    </div>
  );
}

function OptionRow({ row, projects, adminUser, busy, onSave, onDelete, onEdit }) {
  const [edit, setEdit] = React.useState(false);
  const [label, setLabel] = React.useState(row.label || "");
  const [percent, setPercent] = React.useState(String(row.percent ?? ""));

  React.useEffect(() => {
    setLabel(row.label || "");
    setPercent(String(row.percent ?? ""));
  }, [row.id, row.label, row.percent]);

  return (
    <tr>
      <td className="td-muted">{row.id}</td>
      <td>
        {edit ? (
          <input className="input input--small" value={label} onChange={(e) => setLabel(e.target.value)} />
        ) : (
          row.label
        )}
      </td>
      <td>
        {edit ? (
          <input
            className="input input--small"
            inputMode="decimal"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
          />
        ) : (
          `${Number(row.percent || 0).toFixed(1).replace(".", ",")} %`
        )}
      </td>
      <td>
        {edit ? (
          <div className="rowActions">
            <button
              className="btn btn--primary btn--small"
              disabled={busy}
              onClick={() => {
                onSave({ label, percent });
                setEdit(false);
              }}
              type="button"
              title="Speichern"
            >
              💾
            </button>
            <button className="btn btn--ghost btn--small" disabled={busy} onClick={() => setEdit(false)} type="button" title="Abbrechen">
              ❌
            </button>
            <button className="btn btn--ghost btn--small" disabled={busy} onClick={onDelete} type="button" title="Löschen">
              🗑️
            </button>
          </div>
        ) : (
          <div className="rowActions">
            <button className="btn btn--ghost btn--small" disabled={busy} onClick={() => setEdit(true)} type="button" title="Bearbeiten">
              ✏️
            </button>
            <button 
              className="btn btn--small btn--ghost" 
              onClick={onEdit} 
              disabled={busy}
              title="Projekte zuweisen"
            >
              📋
            </button>
            <button className="btn btn--ghost btn--small" disabled={busy} onClick={onDelete} type="button" title="Löschen">
              🗑️
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function EditOptionModal({ option, projects, adminUser, onClose, onUpdate }) {
  const [selectedProjects, setSelectedProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [selectAll, setSelectAll] = React.useState(false);

  React.useEffect(() => {
    async function loadOptionProjects() {
      try {
        const res = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            action: "get_option_projects", 
            admin_id: adminUser.id,
            option_id: option.id 
          }),
        });
        const data = await res.json();
        if (res.ok && data.project_ids) {
          setSelectedProjects(data.project_ids);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Option-Projekte:", error);
      } finally {
        setLoading(false);
      }
    }
    if (option && adminUser) {
      loadOptionProjects();
    }
  }, [option, adminUser]);

  React.useEffect(() => {
    if (selectAll) {
      setSelectedProjects(projects.map(p => p.id));
    } else if (!selectAll && selectedProjects.length === projects.length) {
      setSelectedProjects([]);
    }
  }, [selectAll, projects]);

  function toggleProject(projectId) {
    setSelectedProjects(prev => 
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
    setSelectAll(false);
  }

  async function handleSave() {
    setBusy(true);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          action: "assign_option_projects",
          admin_id: adminUser.id,
          option_id: option.id,
          project_ids: selectedProjects 
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Fehler beim Speichern der Projekte");
      }
      onUpdate();
    } catch (error) {
      console.error("Fehler:", error);
      alert("Fehler beim Speichern der Projekte: " + error.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <section className="card">
          <div className="card__head">
            <h2 className="h2">Option Projekte zuweisen</h2>
            <p className="muted">
              <strong>{option.label}</strong> ({option.percent}%)
            </p>
          </div>
          <div className="card__body">
            {loading ? (
              <div className="muted">Lade Projekte...</div>
            ) : (
              <>
                <div className="field">
                  <label className="extraitem" style={{ marginBottom: '12px' }}>
                    <input
                      className="extraitem__check"
                      type="checkbox"
                      checked={selectAll || selectedProjects.length === projects.length}
                      onChange={(e) => setSelectAll(e.target.checked)}
                    />
                    <span className="extraitem__label" style={{ fontWeight: 'bold' }}>
                      {selectedProjects.length === projects.length ? 'Alle abwählen' : 'Alle auswählen'}
                    </span>
                  </label>
                  <div className="extras__grid" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {projects.map((p) => (
                      <label
                        key={p.id}
                        className={`extraitem ${selectedProjects.includes(p.id) ? "is-on" : ""}`}
                      >
                        <input
                          className="extraitem__check"
                          type="checkbox"
                          checked={selectedProjects.includes(p.id)}
                          onChange={() => toggleProject(p.id)}
                        />
                        <span className="extraitem__label">{p.name}</span>
                        {p.adresse && (
                          <span className="extraitem__amount" style={{ fontSize: '0.8em', opacity: 0.7 }}>
                            {p.adresse.substring(0, 20)}...
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="modalActions" style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
                    Abbrechen
                  </button>
                  <button 
                    className="btn btn--primary" 
                    onClick={handleSave} 
                    disabled={busy || loading}
                  >
                    {busy ? "Speichere…" : "Speichern"}
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

/* =========================
   Admin: Show Employees
========================= */
function ShowEmployees({ adminUser }) {
  const [rows, setRows] = React.useState([]);
  const [msg, setMsg] = React.useState("Lade Mitarbeiter...");
  const [busy, setBusy] = React.useState(false);
  const [showResetModal, setShowResetModal] = React.useState(false);

  async function load() {
    setBusy(true);
    setMsg("Lade Mitarbeiter...");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "list_employees_admin", admin_id: adminUser.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Unbekannter Fehler");
      setRows(Array.isArray(data.employees) ? data.employees : []);
      setMsg("");
    } catch (e) {
      setMsg("❌ " + (e?.message || "Fehler"));
      setRows([]);
    } finally {
      setBusy(false);
    }
  }

  async function doResetMonth() {
    setBusy(true);
    setMsg("");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "reset_month", admin_id: adminUser.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Reset fehlgeschlagen");
      await load();
      setMsg("✅ Monat zurückgesetzt");
    } catch (e) {
      setMsg("❌ " + (e?.message || "Fehler"));
    } finally {
      setBusy(false);
      setShowResetModal(false);
    }
  }

  React.useEffect(() => { load(); }, []);

  return (
    <div id="showEmployees" className="panel">
      <section className="card" style={{ borderRadius: "unset", border: "unset", boxShadow: "unset", overflow: "unset", background: "unset" }}>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn--primary" onClick={() => setShowResetModal(true)} disabled={busy}>
            Monat zurücksetzen
          </button>
        </div>
        {msg ? <div className="notice notice--info">{msg}</div> : null}
        {!msg && rows.length === 0 ? <div className="notice notice--info">Keine Mitarbeiter gefunden.</div> : null}
      </section>
      <div className="spacer" />
      {rows.length > 0 ? (
        <section className="card">
          <div className="tablewrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 160 }}>Mitarbeiter</th>
                  <th style={{ width: 160 }}>Gehalt</th>
                  <th style={{ width: 160}}>Prov.</th>
                  <th style={{ width: 180 }}>Endgehalt</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name} {u.nachname}</td>
                    <td>{Number(u.gehalt || 0).toFixed(2).replace(".", ",")} €</td>
                    <td>+{Number(u.provisionmonat || 0).toFixed(2).replace(".", ",")} €</td>
                    <td><b>{Number(u.summe || 0).toFixed(2).replace(".", ",")} €</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {showResetModal ? (
        <div className="modal-overlay" onClick={() => !busy && setShowResetModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <section className="card">
              <div className="card__head">
                <h2 className="h2">Monat zurücksetzen</h2>
                <p className="muted">
                  Dabei passiert:
                  <br />- Provision wird bei ALLEN Mitarbeitern auf 0 gesetzt
                  <br />- ALLE Anfragen werden gelöscht
                </p>
              </div>
              <div className="card__body">
                <div className="modalActions">
                  <button className="btn btn--ghost" type="button" disabled={busy} onClick={() => setShowResetModal(false)}>
                    Abbrechen
                  </button>
                  <button className="btn btn--primary" type="button" disabled={busy} onClick={doResetMonth}>
                    {busy ? "Setze zurück…" : "Ja, zurücksetzen"}
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* =========================
   Admin: Manage Employees
========================= */
function ManageEmployees({ adminUser }) {
  const [rows, setRows] = React.useState([]);
  const [msg, setMsg] = React.useState("Lade Mitarbeiter...");
  const [busy, setBusy] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState(null);
  const [confirmDelete, setConfirmDelete] = React.useState(null);
  const [projects, setProjects] = React.useState([]);

  async function loadProjects() {
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "list_projects" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Fehler beim Laden der Projekte");
      setProjects(Array.isArray(data.projects) ? data.projects : []);
    } catch (e) {
      console.error(e);
    }
  }

  async function load() {
    setBusy(true);
    setMsg("Lade Mitarbeiter...");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "list_employees_admin", admin_id: adminUser.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Unbekannter Fehler");
      setRows(Array.isArray(data.employees) ? data.employees : []);
      setMsg("");
    } catch (e) {
      setMsg("❌ " + (e?.message || "Fehler"));
      setRows([]);
    } finally {
      setBusy(false);
    }
  }

  async function updateUser(user) {
    setBusy(true);
    setMsg("");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_user", admin_id: adminUser.id, user_id: user.id, ...user }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Unbekannter Fehler");
      await load();
      setMsg("✅ Mitarbeiter aktualisiert");
      setEditingUser(null);
    } catch (e) {
      setMsg("❌ " + (e?.message || "Fehler"));
    } finally {
      setBusy(false);
    }
  }

  async function deleteUser(userId) {
    setBusy(true);
    setMsg("");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete_user", admin_id: adminUser.id, user_id: userId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Unbekannter Fehler");
      await load();
      setMsg("✅ Mitarbeiter gelöscht");
    } catch (e) {
      setMsg("❌ " + (e?.message || "Fehler"));
    } finally {
      setBusy(false);
    }
  }

  React.useEffect(() => {
    load();
    loadProjects();
  }, []);

  return (
    <div id="manageEmployees" className="panel">
      <section className="card" style={{ borderRadius: "unset", border: "unset", boxShadow: "unset", overflow: "unset", background: "unset" }}>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn--primary" onClick={load} disabled={busy}>
            Aktualisieren
          </button>
        </div>
        {msg ? <div className="notice notice--info">{msg}</div> : null}
        {!msg && rows.length === 0 ? <div className="notice notice--info">Keine Mitarbeiter gefunden.</div> : null}
      </section>
      <div className="spacer" />
      {rows.length > 0 ? (
        <section className="card">
          <div className="tablewrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Mitarbeiter</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name} {u.nachname}</td>
                    <td>
                      <div className="rowActions">
                        <button className="btn btn--small btn--ghost" onClick={() => setEditingUser(u)} disabled={busy} title="Bearbeiten">
                          ✏️
                        </button>
                        <button className="btn btn--small btn--ghost" onClick={() => setConfirmDelete(u.id)} disabled={busy} title="Löschen">
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {editingUser ? (
        <EditUserModal 
          user={editingUser} 
          projects={projects} 
          adminUser={adminUser}
          onSave={updateUser} 
          onDelete={deleteUser} 
          onCancel={() => setEditingUser(null)} 
          busy={busy} 
        />
      ) : null}
      {confirmDelete ? (
        <ConfirmModal
          title="Mitarbeiter löschen"
          message="Diesen Mitarbeiter wirklich löschen?"
          onConfirm={() => {
            deleteUser(confirmDelete);
            setConfirmDelete(null);
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      ) : null}
    </div>
  );
}

function EditUserModal({ user, projects, adminUser, onSave, onDelete, onCancel, busy }) {
  const [form, setForm] = React.useState({
    name: user.name || "",
    nachname: user.nachname || "",
    gehalt: user.gehalt || "",
    selectedProjects: [],
  });
  const [loadingProjects, setLoadingProjects] = React.useState(true);
  const [selectAll, setSelectAll] = React.useState(false);

  React.useEffect(() => {
    async function loadUserProjects() {
      try {
        const res = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            action: "get_user_projects", 
            admin_id: adminUser.id,
            user_id: user.id 
          }),
        });
        const data = await res.json();
        if (res.ok && data.project_ids) {
          setForm(prev => ({
            ...prev,
            selectedProjects: data.project_ids
          }));
        }
      } catch (error) {
        console.error("Fehler beim Laden der User-Projekte:", error);
      } finally {
        setLoadingProjects(false);
      }
    }
    if (user && adminUser) {
      loadUserProjects();
    }
  }, [user, adminUser]);

  React.useEffect(() => {
    if (selectAll) {
      setForm(prev => ({
        ...prev,
        selectedProjects: projects.map(p => p.id)
      }));
    } else if (!selectAll && form.selectedProjects.length === projects.length) {
      setForm(prev => ({
        ...prev,
        selectedProjects: []
      }));
    }
  }, [selectAll, projects]);

  function onChange(e) {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  }

  function toggleProject(projectId) {
    setForm((p) => ({
      ...p,
      selectedProjects: p.selectedProjects.includes(projectId)
        ? p.selectedProjects.filter(id => id !== projectId)
        : [...p.selectedProjects, projectId]
    }));
    setSelectAll(false);
  }

  async function handleSave() {
    await onSave({ 
      id: user.id,
      name: form.name, 
      nachname: form.nachname, 
      gehalt: form.gehalt 
    });
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          action: "assign_user_projects",
          admin_id: adminUser.id,
          user_id: user.id,
          project_ids: form.selectedProjects 
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Fehler beim Speichern der Projekte");
      }
      onCancel();
    } catch (error) {
      console.error("Fehler:", error);
      alert("Fehler beim Speichern der Projekte: " + error.message);
    }
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <section className="card">
          <div className="card__head">
            <h2 className="h2">Mitarbeiter verwalten</h2>
          </div>
          <div className="card__body">
            <div className="grid2" style={{ marginBottom: '16px' }}>
              <div className="field">
                <label className="label">Vorname</label>
                <input className="input" name="name" value={form.name} onChange={onChange} required />
              </div>
              <div className="field">
                <label className="label">Nachname</label>
                <input className="input" name="nachname" value={form.nachname} onChange={onChange} required />
              </div>
            </div>
            <div className="field" style={{ marginBottom: '16px' }}>
              <label className="label">Gehalt (€)</label>
              <input className="input" name="gehalt" inputMode="decimal" value={form.gehalt} onChange={onChange} />
            </div>
            <div className="field">
              <label className="label">Projekte zuweisen</label>
              {loadingProjects ? (
                <div className="muted">Lade Projekte...</div>
              ) : (
                <>
                  <label className="extraitem" style={{ marginBottom: '12px' }}>
                    <input
                      className="extraitem__check"
                      type="checkbox"
                      checked={selectAll || form.selectedProjects.length === projects.length}
                      onChange={(e) => setSelectAll(e.target.checked)}
                    />
                    <span className="extraitem__label" style={{ fontWeight: 'bold' }}>
                      {form.selectedProjects.length === projects.length ? 'Alle abwählen' : 'Alle auswählen'}
                    </span>
                  </label>
                  <div className="extras__grid" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {projects.map((p) => (
                      <label key={p.id} className={`extraitem ${form.selectedProjects.includes(p.id) ? "is-on" : ""}`}>
                        <input
                          className="extraitem__check"
                          type="checkbox"
                          checked={form.selectedProjects.includes(p.id)}
                          onChange={() => toggleProject(p.id)}
                        />
                        <span className="extraitem__label">{p.name}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="modalActions" style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn--ghost" onClick={onCancel} disabled={busy}>
                Abbrechen
              </button>
              <button className="btn btn--primary" onClick={handleSave} disabled={busy || loadingProjects}>
                {busy ? "Speichere…" : "Speichern"}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/* =========================
   Confirm Modal
========================= */
function ConfirmModal({ title, message, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <section className="card">
          <div className="card__head">
            <h2 className="h2">{title}</h2>
          </div>
          <div className="card__body">
            <p>{message}</p>
            <div className="modalActions">
              <button className="btn btn--ghost" onClick={onCancel}>
                Abbrechen
              </button>
              <button className="btn btn--primary" onClick={onConfirm}>
                Bestätigen
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ========= Boot ========= */
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);